import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button, CounterBadge, makeStyles } from "@fluentui/react-components";
import { People24Filled } from "@fluentui/react-icons";
import FluentParticipantOverlay from "../room/FluentParticipantsOverlay";
import { useMicrophoneStatus } from "../room/useMicrophoneStatus";
import FluentSettingParticipantOverlay from "../room/FluentSettingParticipantOverlay";
import { useSocialContext } from "../contexts/SocialContext";
import { MediaDevices } from "../../utils/media-devices-utils";

const useStyles = makeStyles({
  button: {
    height: "40px",
    minWidth: "40px",
    backgroundColor: "var(--toolbar-overlay-background)",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "var(--color-text-primary)",
      fill: "var(--color-text-primary)"
    }
  },
  buttonActive: {
    backgroundColor: "var(--primaryColorBackground)",
    height: "40px",
    minWidth: "40px",
    "& span": {
      width: "24px",
      height: "24px",
      fontSize: "24px"
    },
    "& svg": {
      color: "var(--color-text-primary)",
      fill: "var(--color-text-primary)"
    }
  }
});

export default function ToolbarSocialButton({
  scene,
  hubChannel,
  presences,
  mySessionId,
  isOpenParticipants,
  toggleParticipants,
  closeParticipants,
  people,
  agents,
  canOpenChat,
  participantLayoutRoomEnabled,
  participantLayoutForcedState,
  onParticipantLayoutRoomStateChange
}) {
  const { toggleMute } = useMicrophoneStatus(scene);
  const [selectedPerson, setSelectedPerson] = useState(null);

  const { onOpenSettings, setIsOpenSettings, isOpenSettings } = useSocialContext();

  const allMuted = people.every(person => person?.micPresence === undefined || person?.micPresence?.muted);
  const allHost = people
    .filter(person => !person.isMe && person?.roles?.signed_in)
    .every(person => person?.roles?.owner);

  const { privateUnreadMessages } = useSocialContext();

  const selectPerson = person => {
    if (person) {
      const selectedPersonFromPeople = people.find(p => p.id === person.id);
      setSelectedPerson(selectedPersonFromPeople);
    } else {
      setSelectedPerson(null);
    }
    onOpenSettings(true);
  };

  useEffect(() => {
    if (selectedPerson) {
      const updatedPerson = people.find(p => p.id === selectedPerson.id);
      if (updatedPerson) {
        setSelectedPerson(updatedPerson);
      }
    }
  }, [people, selectedPerson]);

  const styles = useStyles();
  const canModerateMedia = hubChannel.canOrWillIfCreator("mute_users");

  const onModeratePeerMedia = useCallback(
    (person, source) => {
      if (source !== MediaDevices.CAMERA && source !== MediaDevices.SCREEN) {
        return;
      }

      if (!canModerateMedia || person.isMe || person.presence !== "room") {
        return;
      }

      return APP.dialog?.moderatePeerMedia?.(person.id, source);
    },
    [canModerateMedia]
  );

  const onModeratePeerMediaAll = useCallback(
    (source, targetPeople = people) => {
      for (const person of targetPeople) {
        onModeratePeerMedia(person, source);
      }
    },
    [onModeratePeerMedia, people]
  );

  const onMute = useCallback(
    person => {
      if (person.isMe && !person.micPresence.muted) {
        toggleMute();
      }

      if (person.presence === "room" && person.permissions) {
        hubChannel.mute(person.id);
      }
    },
    [hubChannel, toggleMute]
  );

  const onMuteAll = useCallback(() => {
    for (const person of people) {
      if (person.isMe && !person.micPresence.muted) {
        toggleMute();
      } else if (person.presence === "room" && person.permissions && !person.roles.creator) {
        hubChannel.mute(person.id);
      }
    }
  }, [people, toggleMute, hubChannel]);

  const closeSettings = useCallback(() => {
    setIsOpenSettings(false);
  }, [setIsOpenSettings]);

  useEffect(() => {
    if (selectedPerson && !people.some(person => person.id === selectedPerson.id)) {
      closeSettings();
    }
  }, [selectedPerson, people, closeSettings]);

  return (
    <>
      <FluentParticipantOverlay
        people={people}
        isOpen={isOpenParticipants}
        scene={scene}
        hubChannel={hubChannel}
        presences={presences}
        mySessionId={mySessionId}
        setIsOpen={toggleParticipants}
        closeParticipants={closeParticipants}
        onMute={onMute}
        onMuteAll={onMuteAll}
        onModeratePeerMedia={onModeratePeerMedia}
        selectPerson={selectPerson}
        agents={agents}
        canOpenChat={canOpenChat}
      />

      <FluentSettingParticipantOverlay
        hubChannel={hubChannel}
        isOpen={isOpenSettings}
        onClose={closeSettings}
        scene={scene}
        person={selectedPerson}
        onMute={onMute}
        allMuted={allMuted}
        people={people}
        onMuteAll={onMuteAll}
        onModeratePeerMedia={onModeratePeerMedia}
        onModeratePeerMediaAll={onModeratePeerMediaAll}
        allHost={allHost}
        mySessionId={mySessionId}
        participantLayoutRoomEnabled={participantLayoutRoomEnabled}
        participantLayoutForcedState={participantLayoutForcedState}
        onParticipantLayoutRoomStateChange={onParticipantLayoutRoomStateChange}
      />

      <div style={{ position: "relative" }}>
        <Button
          appearance="subtle"
          onClick={() => toggleParticipants()}
          className={isOpenParticipants ? styles.buttonActive : styles.button}
          icon={<People24Filled style={{ width: "24px", height: "24px" }} />}
        />
        {/* Add the CounterBadge for participant count */}
        <CounterBadge count={people.length} size="small" style={{ position: "absolute", top: "-3px", right: "-3px" }} />
        {canOpenChat && privateUnreadMessages != false && (
          <Badge size="small" style={{ position: "absolute", top: "-3px", right: "-3px" }} />
        )}
      </div>
    </>
  );
}

ToolbarSocialButton.propTypes = {
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired,
  presences: PropTypes.array,
  mySessionId: PropTypes.string.isRequired,
  isOpenParticipants: PropTypes.bool.isRequired,
  toggleParticipants: PropTypes.func.isRequired,
  closeParticipants: PropTypes.func.isRequired,
  people: PropTypes.array.isRequired,
  agents: PropTypes.array.isRequired,
  canOpenChat: PropTypes.bool,
  participantLayoutRoomEnabled: PropTypes.bool,
  participantLayoutForcedState: PropTypes.string,
  onParticipantLayoutRoomStateChange: PropTypes.func
};
